import { useState } from "react";
import {
  Modal,
  Typography,
  Space,
  Button,
  Input,
  Alert,
  Divider,
  Card,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  LinkOutlined,
  UserOutlined,
  BankOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const AdminCreatedSuccessModal = ({ visible, onClose, adminData }) => {
  const [copying, setCopying] = useState(false);

  const handleCopyLink = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(adminData?.verificationLink || "");
      message.success("Setup link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy:", error);
      message.error("Failed to copy link. Please copy manually.");
    } finally {
      setCopying(false);
    }
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return "24 hours from creation";
    return new Date(dateString).toLocaleString();
  };

  if (!adminData) return null;

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "24px" }} />
          <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
            Admin Created Successfully!
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose} size="large">
          Done
        </Button>,
      ]}
      width={700}
      destroyOnClose
    >
      <div style={{ padding: "16px 0" }}>
        {/* Success Alert */}
        <Alert
          message="Admin User Created"
          description={`${adminData.name} has been created as an admin user. Share the setup link below so they can set their password and activate their account.`}
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* Admin Details */}
        <Card
          title={
            <Space>
              <UserOutlined />
              <span>Admin Details</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <Text strong>Name:</Text>
              <br />
              <Text>{adminData.name}</Text>
            </div>
            <div>
              <Text strong>Email:</Text>
              <br />
              <Text>{adminData.email}</Text>
            </div>
            <div>
              <Text strong>Role:</Text>
              <br />
              <Text style={{ textTransform: "capitalize" }}>
                {adminData.role}
              </Text>
            </div>
            <div>
              <Text strong>Company:</Text>
              <br />
              <Text>{adminData.company || "N/A"}</Text>
            </div>
          </div>
        </Card>

        {/* Setup Link Section */}
        <Card
          title={
            <Space>
              <LinkOutlined />
              <span>Set Password Link</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message="Important Instructions"
            description="Copy and share this secure link with the admin. They will use it to set their password and activate their account."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ marginBottom: 16 }}>
            <Text strong>Setup Link:</Text>
            <Input.Group compact style={{ marginTop: 8 }}>
              <Input
                value={adminData.verificationLink}
                readOnly
                style={{ width: "calc(100% - 120px)" }}
                size="large"
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleCopyLink}
                loading={copying}
                size="large"
                style={{ width: "120px" }}
              >
                Copy Link
              </Button>
            </Input.Group>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#666",
            }}
          >
            <ClockCircleOutlined />
            <Text type="secondary">
              Link expires: {formatExpiryDate(adminData.verificationExpires)}
            </Text>
          </div>
        </Card>

        {/* Next Steps */}
        <Card
          title={
            <Space>
              <BankOutlined />
              <span>Next Steps</span>
            </Space>
          }
        >
          <div style={{ lineHeight: "1.8" }}>
            <Paragraph>
              <Text strong>1. Share the Link:</Text> Send the setup link to{" "}
              {adminData.name} via email, WhatsApp, or any secure channel.
            </Paragraph>
            <Paragraph>
              <Text strong>2. Set Password:</Text> The admin will open the link
              and set their password.
            </Paragraph>
            <Paragraph>
              <Text strong>3. Account Activation:</Text> Once the password is
              set, the account becomes active automatically.
            </Paragraph>
            <Paragraph>
              <Text strong>4. Login:</Text> The admin can then login at{" "}
              <Text code>https://app.nestpedia.info</Text>
            </Paragraph>
          </div>
        </Card>

        <Divider />

        {/* Security Note */}
        <Alert
          message="Security Note"
          description="This setup link is valid for a limited time. If it expires, you can generate a new one from the admin management section."
          type="info"
          showIcon
        />
      </div>
    </Modal>
  );
};

export default AdminCreatedSuccessModal;
